import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Heart, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import NonprofitBadge from "@/components/NonprofitBadge";

/**
 * NonprofitStatusCard
 * -------------------
 * A compact dashboard nudge card that shows:
 *   - Verified nonprofit? → small "You're verified" note + link to view discount
 *   - Pending review?     → "Awaiting admin review" state
 *   - Rejected?           → "Re-submit" prompt
 *   - Not started?        → "Get verified — 50% off Pro forever" nudge
 *
 * Renders nothing while the user isn't loaded yet.
 */
export default function NonprofitStatusCard() {
  const { user } = useAuth();
  if (!user) return null;
  const s = user.nonprofit_status || "unverified";
  return (
    <div
      className="card-warm rounded-xl p-5"
      data-testid="nonprofit-status-card"
      style={{
        borderLeft: `3px solid ${
          s === "verified" ? "#84CC16"
          : s === "pending_review" ? "var(--avt-orange)"
          : s === "rejected" ? "var(--avt-pink)"
          : "var(--avt-teal)"
        }`,
      }}
    >
      <div className="flex items-start gap-3">
        <Heart
          className="h-5 w-5 mt-0.5 flex-shrink-0"
          fill={s === "verified" ? "#84CC16" : "transparent"}
          strokeWidth={2}
          color={s === "verified" ? "#84CC16" : "var(--avt-purple)"}
        />
        <div className="flex-1 min-w-0">
          {s === "verified" && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-serif text-lg">Nonprofit verified</span>
                <NonprofitBadge user={user} />
              </div>
              <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
                {user.nonprofit_org_name || "Your organization"} is verified —
                enjoy <strong>50% off Pro forever</strong> + priority discovery placement.
              </p>
              <Link
                to="/verify-nonprofit"
                data-testid="nonprofit-status-manage"
                className="mt-2 inline-block text-xs underline"
                style={{ color: "var(--avt-teal)" }}
              >
                View details
              </Link>
            </>
          )}
          {s === "pending_review" && (
            <>
              <div className="inline-flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" style={{ color: "var(--avt-orange)" }} />
                <span className="font-serif text-lg">Nonprofit review in progress</span>
              </div>
              <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
                Our team is reviewing your submission — usually within one business day. You&rsquo;ll get an email once approved.
              </p>
              <Link
                to="/verify-nonprofit"
                data-testid="nonprofit-status-view"
                className="mt-2 inline-block text-xs underline"
                style={{ color: "var(--avt-orange)" }}
              >
                View submission
              </Link>
            </>
          )}
          {s === "rejected" && (
            <>
              <div className="inline-flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" style={{ color: "var(--avt-pink)" }} />
                <span className="font-serif text-lg">Verification not approved</span>
              </div>
              <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
                {user.nonprofit_rejection_reason || "Please re-submit with updated documentation."}
              </p>
              <Link
                to="/verify-nonprofit"
                data-testid="nonprofit-status-resubmit"
                className="mt-2 btn-outline inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full"
              >
                Re-submit
              </Link>
            </>
          )}
          {s === "unverified" && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <ShieldCheck className="h-4 w-4" style={{ color: "var(--avt-teal)" }} />
                <span className="font-serif text-lg">Are you a nonprofit?</span>
              </div>
              <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
                Verify your 501(c)(3) status and unlock <strong>50% off Pro forever</strong>,
                a verified badge on every card, and priority placement in discovery.
              </p>
              <Link
                to="/verify-nonprofit"
                data-testid="nonprofit-status-start"
                className="mt-3 btn-primary inline-flex items-center gap-1 text-xs px-4 py-2 rounded-full"
              >
                Get verified
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
